import React, { useEffect, useId, useRef, useState } from "react";
import AskPasswordStatus from "./AskPasswordStatus";
import useTranslation from "../../../../hooks/useTranslation";

interface Props {
    status: number;
    verifyPassword: (password: string) => void;
    onCancel: () => void;
}

export default function AskPassword({ status, verifyPassword, onCancel }: Props) {
    const { t } = useTranslation();
    const id = useId();
    const panelRef = useRef<HTMLDivElement>(null);
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const submit = () => {
        if (submitting || !password.trim()) return;

        setSubmitting(true);
        verifyPassword(password);
    };

    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key !== "Escape" || e.defaultPrevented) return;
            if (!panelRef.current?.contains(e.target as Node)) return;

            onCancel();
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [onCancel]);

    return (
        <div
            ref={panelRef}
            role="dialog"
            aria-labelledby={`${id}-header`}
            aria-busy={submitting}
            className="absolute inset-0 z-10 flex h-full w-full items-start justify-center overflow-y-auto bg-surface-2 p-8 pt-20"
        >
            <div className="flex w-full max-w-md flex-col gap-3 rounded-card border border-border bg-surface p-4 shadow-lg">
                <h3 id={`${id}-header`} className="text-base font-semibold text-ink">
                    {t("pdf_viewer.ask_password.header")}
                </h3>
                <div className="text-sm text-ink-muted">
                    {status === AskPasswordStatus.REQUIRE_PASSWORD &&
                        t("pdf_viewer.ask_password.require_password")}
                    {status === AskPasswordStatus.WRONG_PASSWORD && (
                        <span role="alert" className="text-danger">
                            {t("pdf_viewer.ask_password.wrong_password")}
                        </span>
                    )}
                </div>
                <label htmlFor={`${id}-password`} className="sr-only">
                    {t("attributes.user.password")}
                </label>
                <input
                    id={`${id}-password`}
                    type="password"
                    value={password}
                    autoFocus
                    autoComplete="off"
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && submit()}
                    className="block w-full min-w-0 appearance-none rounded-md! border border-border bg-surface px-3 py-2 text-sm text-ink transition-colors focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent/40"
                />
                <div className="flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={onCancel}
                        className="flex shrink-0 appearance-none items-center gap-2 rounded-lg! border-0 bg-transparent px-4 py-2 text-sm font-medium text-ink-muted transition-colors hover:bg-surface-2"
                    >
                        {t("actions.cancel")}
                    </button>
                    <button
                        type="button"
                        title={t("pdf_viewer.ask_password.button_tooltip")}
                        onClick={submit}
                        disabled={!password.trim()}
                        className="flex shrink-0 appearance-none items-center gap-2 rounded-lg! border-0 bg-accent px-4 py-2 text-sm font-medium text-white! transition-colors hover:brightness-110 disabled:opacity-60 disabled:hover:brightness-100"
                    >
                        {submitting
                            ? t("pdf_viewer.ask_password.unlocking")
                            : t("pdf_viewer.ask_password.button_label")}
                    </button>
                </div>
            </div>
        </div>
    );
}
